import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { getMe, ApiError, SESSION_LOST, type Account } from '../account';

export type AccountModal = 'none' | 'signin' | 'order';

interface Ctx {
  account: Account | null;
  refresh: () => void;
  modal: AccountModal;
  setModal: (m: AccountModal) => void;
}

const AccountCtx = createContext<Ctx | null>(null);

export function AccountProvider({ children }: { children: ReactNode }): JSX.Element {
  const [account, setAccount] = useState<Account | null>(null);
  const [modal, setModal] = useState<AccountModal>('none');
  /**
   * Re-read who we are.
   *
   * `/account/me` answers 200 with `account: null` when there is no session, so
   * the ordinary path already handles signing out. The catch is for real
   * failures, and it used to swallow every one of them: the account then kept
   * its last good value while the cookie was gone, and the page went on looking
   * signed in with every panel quietly 401ing behind it.
   *
   * A 401 on /me itself would mean the same thing, so treat it the same.
   * Anything else (offline, a proxy, a 502) leaves the state alone: signing
   * somebody out over a dropped connection is its own bug.
   */
  const refresh = (): void => {
    void getMe()
      .then(setAccount)
      .catch((e: unknown) => {
        if (e instanceof ApiError && e.status === 401) setAccount(null);
      });
  };
  useEffect(refresh, []);

  /**
   * Any endpoint answering 401 means the session died under us.
   *
   * Re-read /me rather than assuming: it is the one answer that decides, and it
   * will say `account: null`, which flips the UI to signed out. One re-check for
   * the whole page, however many panels happened to fail at once.
   */
  useEffect(() => {
    const onLost = (): void => refresh();
    window.addEventListener(SESSION_LOST, onLost);
    return () => window.removeEventListener(SESSION_LOST, onLost);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <AccountCtx.Provider value={{ account, refresh, modal, setModal }}>{children}</AccountCtx.Provider>;
}

export function useAccount(): Ctx {
  const c = useContext(AccountCtx);
  if (!c) throw new Error('useAccount outside AccountProvider');
  return c;
}
